import { ExtendShiftState } from "@clipboard-health/flag-backend-main";
import { PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack, ThemeProvider } from "@mui/material";
import { useWorkplacePhotos } from "@src/appV2/Facilities/api/useWorkplacePhotos";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useEnableGeofenceTracking } from "@src/appV2/Location/geofence/hooks/useEnableGeofenceTracking";
import { GeofenceProvider } from "@src/appV2/Location/geofence/useGeofence";
import { ChartingCredentialsSetUpContainer } from "@src/appV2/redesign/Charting/ChartingCredentialsSetUpContainer";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type UserLocationState } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import SwipeableViews from "react-swipeable-views";

import { PageWrapper } from "../../../components/PageWrapper";
import { SegmentedControl } from "../../../components/SegmentedControl";
import { PageContent } from "../../components/PageContent";
import { MY_SHIFTS_FULL_PATH } from "../../path";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { getGradientByShiftPeriod } from "../utils/getGradientByShiftPeriod";
import { TimekeepingOptionsButton } from "./components/TimekeepingOptionsButton";
import { LateClockOutInstructionsBottomSheet } from "./LateClockOutInstructionsBottomSheet";
import { TimekeepingDetailsSubPage } from "./TimekeepingDetailsSubPage";
import { TimekeepingFocusSubPage } from "./TimekeepingFocusSubPage";
import { hasLocationStateBooleanKey } from "./utils/hasLocationStateBooleanKey";

function ShiftTimekeepingPageContent() {
  const location = useLocation();
  const history = useHistory();

  const { shiftId } = useParams<{ shiftId: string }>();
  const [activeTab, setActiveTab] = useState<"timeline" | "details">("timeline");

  useLogEffect(
    APP_V2_USER_EVENTS.TIMEKEEPING_PAGE_VIEWED,
    { shiftId },
    { enabled: isDefined(shiftId) }
  );

  const clockOutModalState = useModalState();

  const { shift, shiftState, shiftError } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  const currentDate = useCurrentDate(1000);

  const { data: facilityPhotos } = useWorkplacePhotos(
    { workplaceId: shift?.facility?.userId ?? "" },
    { enabled: isDefined(shift?.facility?.userId) }
  );

  const isPageDataError = isDefined(shiftError);

  const workplaceImage = facilityPhotos?.photosList?.[0]?.url;

  function handleTabChange(tab: "timeline" | "details") {
    setActiveTab(tab);

    if (isDefined(shiftId)) {
      if (tab === "timeline") {
        logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_FOCUS_SUBPAGE_VIEWED, { shiftId });
      } else {
        logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_DETAILS_SUBPAGE_VIEWED, { shiftId });
      }
    }
  }

  function handleBack() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_BACK_BUTTON_TAPPED, {
      shiftId,
      activeTab,
    });
    history.replace(MY_SHIFTS_FULL_PATH);
  }

  const justClockedIn = hasLocationStateBooleanKey(location.state, "justClockedIn")
    ? location.state.justClockedIn
    : false;

  const extendShiftConfig = shiftState?.metadata.extendShiftConfig;
  const isExtendShiftEnabled =
    isDefined(extendShiftConfig) && extendShiftConfig.state !== ExtendShiftState.DISABLED;

  const showLateClockOutInstructions = justClockedIn && isExtendShiftEnabled;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => {
          const timeSlotGradient = isDefined(shift?.name)
            ? getGradientByShiftPeriod(shiftPeriodToTimeSlot[shift.name], theme)
            : undefined;

          return {
            background: timeSlotGradient
              ? `linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 50%), ${timeSlotGradient}`
              : "linear-gradient(180deg, #CCE7FF 0%, #F6F3F1 100%)",
            transition: theme.transitions.create(["background"]),
            minHeight: "100vh",
          };
        }}
      >
        {/* TODO: Replace with StickyScrollPageHeader / PageHeaderTopRow */}
        <PageHeaderWrapper sx={{ paddingX: 4, background: "transparent" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            width="100%"
          >
            <IconButton
              aria-label="Go Back"
              title="Go Back"
              iconType="chevron-left"
              variant="outlined"
              onClick={() => {
                handleBack();
              }}
            />
            {isDefined(shift) && isDefined(shiftState) && (
              <SegmentedControl
                ariaLabel="Shift view"
                value={activeTab}
                options={[
                  { label: "Timeline", value: "timeline" },
                  { label: "Details", value: "details" },
                ]}
                onChange={(tab) => {
                  handleTabChange(tab);
                }}
              />
            )}
            <TimekeepingOptionsButton
              shiftId={shiftId}
              workplaceId={shift?.facilityId}
              clockOutModalState={clockOutModalState}
            />
          </Stack>
        </PageHeaderWrapper>
        <PageContent>
          {isPageDataError ? (
            <Alert sx={{ marginY: 4 }} severity="error">
              Something went wrong while loading this shift. Please try again.
            </Alert>
          ) : null}

          {isDefined(shift) && isDefined(shiftState) && (
            <SwipeableViews
              enableMouseEvents
              index={activeTab === "timeline" ? 0 : 1}
              style={{ overflow: "hidden", flexGrow: 1 }}
              onChangeIndex={(index: number) => {
                handleTabChange(index === 0 ? "timeline" : "details");
              }}
            >
              <TimekeepingFocusSubPage
                shift={shift}
                shiftState={shiftState}
                workplaceImage={workplaceImage}
                workplaceName={shift.facility.name}
                currentDate={currentDate}
                clockOutModalState={clockOutModalState}
              />
              <TimekeepingDetailsSubPage shift={shift} />
            </SwipeableViews>
          )}
        </PageContent>
      </PageWrapper>
      {showLateClockOutInstructions && <LateClockOutInstructionsBottomSheet />}
    </ThemeProvider>
  );
}

export function ShiftTimekeepingPage() {
  const location = useLocation();
  const { shiftId } = useParams<{ shiftId: string }>();

  const { userId } = useDefinedWorker();
  const initialShiftStateLocation = useRef<UserLocationState | undefined>(undefined);

  const { shift, shiftState } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  if (isDefined(shiftState) && !initialShiftStateLocation.current) {
    initialShiftStateLocation.current = shiftState.metadata.userLocationState;
  }

  const facilityGeoLocation = isDefined(shift?.facility.geoLocation.coordinates)
    ? convertToGeoLocation(shift?.facility.geoLocation.coordinates)
    : undefined;

  const enableGeofenceTracking = useEnableGeofenceTracking({
    shiftStart: shift?.start,
    shiftState,
  });

  const justClockedOut = hasLocationStateBooleanKey(location.state, "clockedOut")
    ? location.state.clockedOut
    : false;

  return (
    <GeofenceProvider
      shiftId={shiftId ?? ""}
      workerId={userId}
      workplaceId={shift?.facilityId}
      initialLocation={initialShiftStateLocation.current}
      fallbackGeofenceLocation={facilityGeoLocation}
      enableGeofenceTracking={enableGeofenceTracking}
    >
      <ShiftTimekeepingPageContent />
      {!justClockedOut && isDefined(shift?.facilityId) && (
        <ChartingCredentialsSetUpContainer shiftId={shiftId} workplaceId={shift.facilityId} />
      )}
    </GeofenceProvider>
  );
}
